<template>
  <div id="main-wrapper">
    <AdminNavbar />

    <div class="page-title">
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12">
            <h2 class="ipt-title">{{ t('Welcome!') }}</h2>
          </div>
        </div>
      </div>
    </div>

    <section class="bg-light">
      <div class="container-fluid">
        <div class="row">
          <div class="col-lg-3 col-md-12">
            <AdminSidebar :open="show" @update:open="show = $event" :user="user"/>
          </div>

          <div class="col-lg-9 col-md-12">
            <div class="dashboard-wraper">
              <h4 class="mb-3">{{ t('Categories') }}</h4>

              <div class="row g-3">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="col-lg-3 col-md-4 col-sm-6"
                >
                  <div
                    class="category-card"
                    :class="{ selected: selectedCategories.includes(category.id) }"
                    @click="openModal(category)"
                  >
                    <h6 style="color:#6A715D">{{ category.name }}</h6>
                  </div>
                </div>
              </div>

              <!-- Modal -->
              <div v-if="modalCategory" class="modal-overlay" @click.self="closeModal">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5>{{ modalCategory.name }}</h5>
                    <button class="close-btn" @click="closeModal">&times;</button>
                  </div>
                  <div class="modal-body">
                    <p class="description">{{ modalCategory.description }}</p>
                    <p><strong>{{ t('Exhibits') }}:</strong> {{ modalCategory.exhibits_count }}</p>
                  </div>
                  <div class="modal-actions">
                    <button class="btn btn-secondary" @click="closeModal">{{ t('Close') }}</button>
                    <button class="btn btn-primary" @click="markInterested(modalCategory)">
                      {{ selectedCategories.includes(modalCategory.id) ? t('Uninterested') : t('Interested') }}
                    </button>
                  </div>
                </div>
              </div>

              <button class="btn btn-primary mt-4" @click="submitCategories">
                {{ t('Submit') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FooterTop :bg="'theme-bg'" />
    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'

import AdminSidebar from '@/components/admin-sidebar.vue'
import AdminNavbar from '@/components/navbar/admin-navbar.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

const { t,locale } = useI18n()
const toast = useToast()
const router = useRouter()

const show = ref(false)
const user = ref(null)
const categories = ref([])
const selectedCategories = ref([])
const modalCategory = ref(null)

const token = localStorage.getItem('auth_token')

onMounted(async () => {
  if (!token) {
    toast.error(t('You are not logged in.'))
    router.replace('/404')
    return
  }

  try {
    const userResponse = await axios.get('/visitor', {
      headers: { Authorization: `Bearer ${token}` }
    })
    user.value = userResponse.data.user

    const categoriesResponse = await axios.get('/categories', {
      params: { lang: locale.value }, 
      headers: { Authorization: `Bearer ${token}` }
    })
    categories.value = categoriesResponse.data
    selectedCategories.value = categories.value.filter(c => c.selected).map(c => c.id)
  } catch (error) {
    toast.error(t('Failed to load data'))
    router.replace('/404')
  }
})

// Modal functions
const openModal = (category) => { modalCategory.value = category }
const closeModal = () => { modalCategory.value = null }

// Mark category as interested
const markInterested = (category) => {
  const index = selectedCategories.value.indexOf(category.id)

  if (index === -1) {
    selectedCategories.value.push(category.id)
 } else {
    selectedCategories.value.splice(index, 1)
  }
  modalCategory.value = null
}


// Submit selected categories
const submitCategories = async () => {
  try {
    await axios.post(
      '/visitor-categories',
      { categories: selectedCategories.value },
      { 
        params: { lang: locale.value },
        headers: { Authorization: `Bearer ${token}` } 
      }
    )
    toast.success(t('Categories saved successfully'))
  } catch (error) {
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors
        for (const key in errors) {
          toast.error(errors[key][0])
        }
      } else if (error.response && error.response.data.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error(t('Failed to save exhibits'))
      }

      console.error('Submit exhibits error:', error)
    }
  }

</script>

<style scoped>
.categories-wrapper {
  padding: 1rem 0;
}

.category-card {
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fff;
}
.category-card.selected {
  background-color: #C48B6E;
  color: #fff;
}
.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

.modal-content {
  background-color: #fff;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  animation: slideIn 0.3s;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.description {
  color: #555;
  margin-bottom: 1rem;
}

/* Animations */
@keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
@keyframes slideIn { from {transform: translateY(-20px);} to {transform: translateY(0);} }

</style>
